/**
 * An item group's variants as edited on its Variants tab (ADR 0052): dimensions with their values, and the member items
 * with one value per dimension. Rows are keyed locally, so a code can be renamed without losing the members that use
 * it; the whole draft is sent in one save.
 */

export interface DraftValue {
  key: string;
  code: string;
  label: string;
  labelEn: string;
  shortLabel: string;
  swatchColor: string;
}

export interface DraftDimension {
  key: string;
  code: string;
  name: string;
  nameEn: string;
  display: string;
  values: DraftValue[];
}

export interface DraftMember {
  key: string;
  itemId: string;
  itemLabel: string;
  /** Value key per dimension key. */
  values: Record<string, string>;
  isDefault: boolean;
}

export interface VariantDraft {
  dimensions: DraftDimension[];
  members: DraftMember[];
}

type Texts = Record<string, string | null | undefined> | null | undefined;

export interface SavedVariants {
  version: number | string;
  dimensions: {
    code: string;
    name: string;
    nameI18n?: Texts;
    display: string;
    values: { code: string; label: string; labelI18n?: Texts; shortLabel?: string | null; swatchColor?: string | null }[];
  }[];
  members: { itemId: string; itemCode: string; itemName: string; values: Record<string, string>; isDefault: boolean }[];
}

let next = 0;
const newKey = (prefix: string) => `${prefix}-${++next}`;

export function fromVariants(saved: SavedVariants): VariantDraft {
  const dimensions = saved.dimensions.map((d) => ({
    key: newKey('d'),
    code: d.code,
    name: d.name,
    nameEn: d.nameI18n?.en ?? '',
    display: d.display,
    values: d.values.map((v) => ({
      key: newKey('v'),
      code: v.code,
      label: v.label,
      labelEn: v.labelI18n?.en ?? '',
      shortLabel: v.shortLabel ?? '',
      swatchColor: v.swatchColor ?? '',
    })),
  }));
  return {
    dimensions,
    members: saved.members.map((m) => ({
      key: newKey('m'),
      itemId: m.itemId,
      itemLabel: `${m.itemCode} · ${m.itemName}`,
      isDefault: m.isDefault,
      values: Object.fromEntries(
        dimensions.flatMap((d) => {
          const value = d.values.find((v) => v.code === m.values[d.code]);
          return value ? [[d.key, value.key]] : [];
        }),
      ),
    })),
  };
}

const i18n = (en: string) => (en.trim() ? { en: en.trim() } : {});

/** The request body of `PUT /item-groups/{id}/variants`. */
export function toRequest(draft: VariantDraft) {
  return {
    dimensions: draft.dimensions.map((d) => ({
      code: d.code.trim(),
      name: d.name.trim(),
      nameI18n: i18n(d.nameEn),
      display: d.display,
      values: d.values.map((v) => ({
        code: v.code.trim(),
        label: v.label.trim(),
        labelI18n: i18n(v.labelEn),
        shortLabel: v.shortLabel.trim() || null,
        swatchColor: v.swatchColor.trim() || null,
      })),
    })),
    members: draft.members.map((m) => ({
      itemId: m.itemId,
      isDefault: m.isDefault,
      values: Object.fromEntries(
        draft.dimensions.map((d) => [d.code.trim(), d.values.find((v) => v.key === m.values[d.key])?.code.trim() ?? '']),
      ),
    })),
  };
}

export const isDirty = (draft: VariantDraft, saved: SavedVariants) =>
  JSON.stringify(toRequest(draft)) !== JSON.stringify(toRequest(fromVariants(saved)));

export type DraftProblem =
  | 'dimensionCode'
  | 'dimensionName'
  | 'noValues'
  | 'valueCode'
  | 'valueLabel'
  | 'swatch'
  | 'memberValue'
  | 'duplicate'
  | 'needsDimension';

const CODE = /^[A-Za-z0-9_-]{1,50}$/;
const SWATCH = /^#[0-9A-Fa-f]{6}$/;

/** What would be refused on save, checked as the user types (the server checks again). */
export function problems(draft: VariantDraft): Set<DraftProblem> {
  const found = new Set<DraftProblem>();
  const codes = draft.dimensions.map((d) => d.code.trim());
  if (codes.some((c) => !CODE.test(c)) || new Set(codes).size !== codes.length) found.add('dimensionCode');
  for (const d of draft.dimensions) {
    if (!d.name.trim()) found.add('dimensionName');
    if (d.values.length === 0) found.add('noValues');
    const valueCodes = d.values.map((v) => v.code.trim());
    if (valueCodes.some((c) => !CODE.test(c)) || new Set(valueCodes).size !== valueCodes.length) found.add('valueCode');
    if (d.values.some((v) => !v.label.trim())) found.add('valueLabel');
    if (d.values.some((v) => v.swatchColor.trim() && !SWATCH.test(v.swatchColor.trim()))) found.add('swatch');
  }
  if (draft.dimensions.length === 0 && draft.members.length > 1) found.add('needsDimension');
  const combinations = new Set<string>();
  for (const m of draft.members) {
    if (draft.dimensions.some((d) => !d.values.some((v) => v.key === m.values[d.key]))) found.add('memberValue');
    const combination = draft.dimensions.map((d) => m.values[d.key] ?? '').join('|');
    if (draft.dimensions.length > 0 && combinations.has(combination)) found.add('duplicate');
    combinations.add(combination);
  }
  return found;
}

// ---- edits

const without = (values: Record<string, string>, key: string) => Object.fromEntries(Object.entries(values).filter(([k]) => k !== key));

export function addDimension(draft: VariantDraft, code: string, name: string): VariantDraft {
  const value: DraftValue = { key: newKey('v'), code: '', label: '', labelEn: '', shortLabel: '', swatchColor: '' };
  const dimension: DraftDimension = { key: newKey('d'), code, name, nameEn: '', display: 'buttons', values: [value] };
  // Every member needs a value of the new dimension: the first one, to change.
  return {
    dimensions: [...draft.dimensions, dimension],
    members: draft.members.map((m) => ({ ...m, values: { ...m.values, [dimension.key]: value.key } })),
  };
}

export function updateDimension(draft: VariantDraft, key: string, patch: Partial<Omit<DraftDimension, 'key' | 'values'>>): VariantDraft {
  return { ...draft, dimensions: draft.dimensions.map((d) => (d.key === key ? { ...d, ...patch } : d)) };
}

export function removeDimension(draft: VariantDraft, key: string): VariantDraft {
  return {
    dimensions: draft.dimensions.filter((d) => d.key !== key),
    members: draft.members.map((m) => ({ ...m, values: without(m.values, key) })),
  };
}

export function addValue(draft: VariantDraft, dimensionKey: string): VariantDraft {
  const value: DraftValue = { key: newKey('v'), code: '', label: '', labelEn: '', shortLabel: '', swatchColor: '' };
  return {
    ...draft,
    dimensions: draft.dimensions.map((d) => (d.key === dimensionKey ? { ...d, values: [...d.values, value] } : d)),
  };
}

export function updateValue(draft: VariantDraft, dimensionKey: string, valueKey: string, patch: Partial<Omit<DraftValue, 'key'>>): VariantDraft {
  return {
    ...draft,
    dimensions: draft.dimensions.map((d) =>
      d.key === dimensionKey ? { ...d, values: d.values.map((v) => (v.key === valueKey ? { ...v, ...patch } : v)) } : d,
    ),
  };
}

/** Removing a value leaves the members that had it without one, to choose again. */
export function removeValue(draft: VariantDraft, dimensionKey: string, valueKey: string): VariantDraft {
  return {
    dimensions: draft.dimensions.map((d) => (d.key === dimensionKey ? { ...d, values: d.values.filter((v) => v.key !== valueKey) } : d)),
    members: draft.members.map((m) => (m.values[dimensionKey] === valueKey ? { ...m, values: without(m.values, dimensionKey) } : m)),
  };
}

export function addMember(draft: VariantDraft, itemId: string, itemLabel: string): VariantDraft {
  if (draft.members.some((m) => m.itemId === itemId)) return draft;
  const member: DraftMember = {
    key: newKey('m'),
    itemId,
    itemLabel,
    isDefault: draft.members.length === 0,
    values: Object.fromEntries(draft.dimensions.flatMap((d) => (d.values[0] ? [[d.key, d.values[0].key]] : []))),
  };
  return { ...draft, members: [...draft.members, member] };
}

export function setMemberValue(draft: VariantDraft, memberKey: string, dimensionKey: string, valueKey: string): VariantDraft {
  return { ...draft, members: draft.members.map((m) => (m.key === memberKey ? { ...m, values: { ...m.values, [dimensionKey]: valueKey } } : m)) };
}

export function setDefault(draft: VariantDraft, memberKey: string): VariantDraft {
  return { ...draft, members: draft.members.map((m) => ({ ...m, isDefault: m.key === memberKey })) };
}

export function removeMember(draft: VariantDraft, memberKey: string): VariantDraft {
  const members = draft.members.filter((m) => m.key !== memberKey);
  // The default goes to the first one left.
  return {
    ...draft,
    members: members.some((m) => m.isDefault) ? members : members.map((m, i) => ({ ...m, isDefault: i === 0 })),
  };
}

export function moveMember(draft: VariantDraft, memberKey: string, by: -1 | 1): VariantDraft {
  const index = draft.members.findIndex((m) => m.key === memberKey);
  const target = index + by;
  if (index < 0 || target < 0 || target >= draft.members.length) return draft;
  const members = [...draft.members];
  const [moved] = members.splice(index, 1);
  if (moved) members.splice(target, 0, moved);
  return { ...draft, members };
}

/** A suggested group (from item names with a size tag) as a draft with one "size" dimension. */
export function fromSuggestion(members: { itemId: string; itemCode: string; itemName: string; value: string; isDefault: boolean }[]): VariantDraft {
  let draft = addDimension({ dimensions: [], members: [] }, 'size', 'Kích thước');
  const dimension = draft.dimensions[0];
  if (!dimension) return draft;
  draft = { ...draft, dimensions: [{ ...dimension, values: [] }] };
  for (const m of members) {
    if (!draft.dimensions[0]?.values.some((v) => v.code === m.value)) {
      draft = addValue(draft, dimension.key);
      const added = draft.dimensions[0]?.values.at(-1);
      if (added) draft = updateValue(draft, dimension.key, added.key, { code: m.value, label: m.value, shortLabel: m.value });
    }
  }
  for (const m of members) {
    draft = addMember(draft, m.itemId, `${m.itemCode} · ${m.itemName}`);
    const member = draft.members.at(-1);
    const value = draft.dimensions[0]?.values.find((v) => v.code === m.value);
    if (member && value) draft = setMemberValue(draft, member.key, dimension.key, value.key);
    if (member && m.isDefault) draft = setDefault(draft, member.key);
  }
  return draft;
}
